import React, { useCallback, useEffect, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_TAB_PROMOTION_DEVICE_TYPE_OPTIONS,
    APP_TAB_PROMOTION_NAV_TAB_OPTIONS,
    APP_TAB_PROMOTION_PLACEMENT_OPTIONS,
    APP_TAB_PROMOTION_ROUTE_TARGET_OPTIONS,
    APP_TAB_PROMOTION_STATUS_OPTIONS,
    APP_TAB_PROMOTION_TARGET_MEMBERSHIP_OPTIONS,
} from './appTabPromotionConstants'

const IMAGE_FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/png,image/gif,image/jpeg,image/webp'
const IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'
const IMAGE_LEAVE_MESSAGE = '선택한 프로모션 이미지는 저장 전에 이동하면 사라집니다. 이동하시겠습니까?'
const NUMBER_FIELDS = ['priority', 'status']

const DEFAULT_PARAMS = {
    navTab: 'contest',
    placement: 'tab_enter_modal',
    title: '',
    image: null,
    imageWidth: null,
    imageHeight: null,
    routeTarget: 'membership',
    templateKey: 'membership.contest_tab_promotion',
    landingUrl: '',
    targetMembership: 'non_entitled',
    cCode: '00',
    deviceType: 'all',
    appMode: 'rm',
    priority: 0,
    status: 1,
    startTime: '',
    endTime: '',
}

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const hasFileConstructor = () => typeof File !== 'undefined'

const isImageFileObject = (value) => hasFileConstructor() && value instanceof File

const isImageFile = (file) => Boolean(file) && (
    (file.type && file.type.startsWith('image/')) ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name || '')
)

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') return ''
    return imageFile.name || ''
}

const getImageDimensionText = (imageWidth, imageHeight) => {
    const width = Number(imageWidth)
    const height = Number(imageHeight)

    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return ''
    return `${width}x${height}`
}

const normalizeText = (value) => {
    if (value === undefined || value === null) return ''
    return String(value)
}

const formatDateTimeLocal = (value) => {
    if (!value) return ''
    const normalized = String(value).replace('T', ' ')
    const [datePart, timePart = ''] = normalized.split(' ')
    if (!datePart) return ''
    return `${datePart}T${(timePart || '00:00').slice(0, 5)}`
}

const createParamsFromDetail = (detail = {}) => ({
    ...DEFAULT_PARAMS,
    ...detail,
    image: null,
    imageWidth: Number(detail.imageWidth) || null,
    imageHeight: Number(detail.imageHeight) || null,
    title: normalizeText(detail.title),
    routeTarget: normalizeText(detail.routeTarget),
    templateKey: normalizeText(detail.templateKey),
    landingUrl: normalizeText(detail.landingUrl),
    cCode: normalizeText(detail.cCode || '00').toLowerCase(),
    priority: Number(detail.priority || 0),
    status: Number(detail.status ?? 1),
    startTime: formatDateTimeLocal(detail.startTime),
    endTime: formatDateTimeLocal(detail.endTime),
})

const appendOptionalText = (formData, key, value) => {
    formData.append(key, normalizeText(value).trim())
}

const buildSubmitFormData = ({ params, promotionId, requireImage }) => {
    const nextParams = {
        ...params,
        title: normalizeText(params.title).trim(),
        routeTarget: normalizeText(params.routeTarget).trim(),
        templateKey: normalizeText(params.templateKey).trim(),
        landingUrl: normalizeText(params.landingUrl).trim(),
        cCode: normalizeText(params.cCode || '00').trim().toLowerCase(),
        appMode: normalizeText(params.appMode || 'rm').trim(),
        priority: Number(params.priority || 0),
        status: Number(params.status ?? 1),
    }

    if (requireImage && !isImageFileObject(nextParams.image)) {
        return { nextParams, errorMessage: '이미지를 업로드해주세요.' }
    }

    if (!nextParams.title) {
        return { nextParams, errorMessage: '관리용 제목을 입력하세요.' }
    }

    if (nextParams.routeTarget && nextParams.landingUrl) {
        return { nextParams, errorMessage: '앱 이동 대상과 외부 URL은 동시에 입력할 수 없습니다.' }
    }

    if (!nextParams.cCode || nextParams.cCode.length !== 2) {
        return { nextParams, errorMessage: '국가코드를 2자리로 입력하세요.' }
    }

    if (!nextParams.appMode) {
        return { nextParams, errorMessage: '앱 모드를 입력하세요.' }
    }

    if (!Number.isFinite(nextParams.priority) || nextParams.priority < 0) {
        return { nextParams, errorMessage: '우선순위를 0 이상으로 입력하세요.' }
    }

    if (nextParams.startTime && nextParams.endTime && new Date(nextParams.startTime) > new Date(nextParams.endTime)) {
        return { nextParams, errorMessage: '노출 종료 시간은 시작 시간 이후여야 합니다.' }
    }

    const formData = new FormData()

    if (promotionId) formData.append('promotionId', promotionId)
    if (isImageFileObject(nextParams.image)) formData.append('image', nextParams.image)

    appendOptionalText(formData, 'navTab', nextParams.navTab)
    appendOptionalText(formData, 'placement', nextParams.placement)
    appendOptionalText(formData, 'title', nextParams.title)
    appendOptionalText(formData, 'routeTarget', nextParams.routeTarget)
    appendOptionalText(formData, 'templateKey', nextParams.templateKey)
    appendOptionalText(formData, 'landingUrl', nextParams.landingUrl)
    appendOptionalText(formData, 'targetMembership', nextParams.targetMembership)
    appendOptionalText(formData, 'cCode', nextParams.cCode)
    appendOptionalText(formData, 'deviceType', nextParams.deviceType)
    appendOptionalText(formData, 'appMode', nextParams.appMode)
    formData.append('priority', nextParams.priority)
    formData.append('status', nextParams.status)
    appendOptionalText(formData, 'startTime', nextParams.startTime)
    appendOptionalText(formData, 'endTime', nextParams.endTime)

    return { nextParams, formData, errorMessage: '' }
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">{description}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>
        </Paper>
    )
}

function ImageUploadPanel({ imageSrc, imageFile, imageWidth, imageHeight, disabled, onImageChange }) {
    const imageFileName = getImageFileName(imageFile)
    const imageDimensionText = getImageDimensionText(imageWidth, imageHeight)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>프로모션 이미지</Typography>
                <Typography variant="caption" color="text.secondary">탭 진입 시 앱에 내려줄 이미지를 등록합니다.</Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box component="img" src={imageSrc} alt="프로모션 이미지 미리보기" sx={IMAGE_PREVIEW_SX} />
                ) : (
                    <Typography variant="body2" color="text.secondary">이미지를 선택하면 미리보기가 표시됩니다.</Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField inputProps={{ accept: IMAGE_FILE_ACCEPT }} style={{ display: 'none' }} name="image" id="appTabPromotionImage" type="file" disabled={disabled} onChange={onImageChange} />
                <Box component="label" htmlFor="appTabPromotionImage" sx={{ display: 'block' }}>
                    <Button fullWidth variant="contained" color="success" component="span" startIcon={<Add />} sx={FORM_WIDE_UPLOAD_BUTTON_SX} disabled={disabled}>이미지 업로드</Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {imageDimensionText ? `저장된 크기: ${imageDimensionText}` : '이미지 크기는 저장 후 자동 계산됩니다.'}
            </Typography>
        </Paper>
    )
}

export default function AppTabPromotionFormPage({ mode, promotionId }) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || {}
    const listSearch = location.search || ''
    const isEdit = mode === 'edit'
    const [loading, setLoading] = useState(isEdit)
    const [hasDetail, setHasDetail] = useState(!isEdit)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState(DEFAULT_PARAMS)
    const [imagePreview, setImagePreview] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = isImageFileObject(params.image) && !saving && !navigationUnlocked

    const getData = useCallback(async () => {
        if (!isEdit) return

        if (!promotionId) {
            setHasDetail(false)
            setDetailErrorMessage('필수 파라미터가 없어 App 탭 프로모션 상세를 불러올 수 없습니다.')
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appTabPromotion/getDetail', { promotionId })
            if (data.code === 200) {
                const detail = data.data || {}

                if (!detail.promotionId) {
                    setDetailErrorMessage('App 탭 프로모션 상세 정보를 찾을 수 없습니다.')
                    return
                }

                setParams(createParamsFromDetail(detail))
                setImagePreview(detail.image ? `${process.env.REACT_APP_AWS_S3_URL}/${detail.image}` : '')
                setHasDetail(true)
                return
            }

            if (data.code === 403) {
                history.push('/login')
                return
            }

            setDetailErrorMessage(data.message || 'App 탭 프로모션 상세를 불러오지 못했습니다.')
        } catch (_error) {
            setDetailErrorMessage('App 탭 프로모션 상세를 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [history, isEdit, promotionId])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!leaveGuardEnabled) return undefined

        const handleBeforeUnload = (event) => {
            event.preventDefault()
            event.returnValue = ''
            return ''
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [leaveGuardEnabled])

    const handleClose = () => setOpenSnackbar(false)

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/appTabPromotions',
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                curPage: listCurPage,
                searchText: listSearchText,
                searchSelect: listSearchSelect,
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            setNavigationUnlocked(true)
            return
        }

        if (!window.confirm(IMAGE_LEAVE_MESSAGE)) {
            event.preventDefault()
            return
        }

        setNavigationUnlocked(true)
    }

    const handleUpdateField = (e) => {
        const { name, value } = e.target
        setParams(prev => ({
            ...prev,
            [name]: NUMBER_FIELDS.includes(name) ? Number(value) : value,
        }))
    }

    const handleImageChange = (event) => {
        event.preventDefault()
        const input = event.target
        const file = input.files?.[0]
        if (!file) return

        if (!isImageFile(file)) {
            input.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()
        reader.onloadend = () => {
            setParams(prev => ({ ...prev, image: file, imageWidth: null, imageHeight: null }))
            setImagePreview(reader.result)
        }
        reader.readAsDataURL(file)
    }

    const saveData = async (formData) => {
        try {
            setSaving(true)
            const { data } = await API({
                method: 'post',
                url: isEdit ? '/api/v1/admin/appTabPromotion/update' : '/api/v1/admin/appTabPromotion/create',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            })

            if (data.code === 200) {
                moveToList({ severity: 'success', message: data.message })
                return
            }

            if (data.code === 403) {
                history.push('/login')
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(data.message)
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(`App 탭 프로모션을 ${isEdit ? '저장' : '등록'}하지 못했습니다.`)
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (saving) return

        const { nextParams, errorMessage, formData } = buildSubmitFormData({
            params,
            promotionId: isEdit ? promotionId : '',
            requireImage: !isEdit,
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        saveData(formData)
    }

    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 탭 프로모션 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={<BottomButtonGroup variant="emptyStateAction" path="/appTabPromotions" search={listSearch} visible={false} curPage={listCurPage} searchText={listSearchText} searchSelect={listSearchSelect} backLabel="목록으로" />}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return <Grid item xs={12}><Spiner/></Grid>
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 탭 프로모션 수정</Title>
                    {renderDetailErrorState()}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={IMAGE_LEAVE_MESSAGE} />
                <Title>{isEdit ? 'App 탭 프로모션 수정' : 'App 탭 프로모션 등록'}</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={5}>
                            <ImageUploadPanel imageSrc={imagePreview} imageFile={params.image} imageWidth={params.imageWidth} imageHeight={params.imageHeight} disabled={saving} onImageChange={handleImageChange} />
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <FormSection title="기본 설정" description="노출 탭, 위치, 대상과 관리용 제목을 설정합니다.">
                                <FormControl fullWidth>
                                    <TextField required label="관리용 제목" name="title" value={params.title || ''} onChange={handleUpdateField} disabled={saving} size="small" />
                                </FormControl>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required select label="노출 탭" name="navTab" value={params.navTab} onChange={handleUpdateField} disabled={saving} size="small">
                                            {APP_TAB_PROMOTION_NAV_TAB_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required select label="노출 위치" name="placement" value={params.placement} onChange={handleUpdateField} disabled={saving} size="small">
                                            {APP_TAB_PROMOTION_PLACEMENT_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required select label="멤버십 대상" name="targetMembership" value={params.targetMembership} onChange={handleUpdateField} disabled={saving} size="small">
                                            {APP_TAB_PROMOTION_TARGET_MEMBERSHIP_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required select label="노출 상태" name="status" value={params.status} onChange={handleUpdateField} disabled={saving} size="small">
                                            {APP_TAB_PROMOTION_STATUS_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormSection title="이동 설정" description="이미지를 눌렀을 때 앱 내부 이동 또는 외부 URL 중 하나만 사용합니다.">
                                <TextField fullWidth select label="앱 이동 대상" name="routeTarget" value={params.routeTarget || ''} onChange={handleUpdateField} disabled={saving} size="small">
                                    {APP_TAB_PROMOTION_ROUTE_TARGET_OPTIONS.map(option => <MenuItem key={option.value || 'none'} value={option.value}>{option.label}</MenuItem>)}
                                </TextField>
                                <TextField fullWidth label="템플릿 키" name="templateKey" value={params.templateKey || ''} onChange={handleUpdateField} disabled={saving} size="small" />
                                <TextField fullWidth label="외부 URL" name="landingUrl" value={params.landingUrl || ''} onChange={handleUpdateField} disabled={saving} size="small" />
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormSection title="노출 조건" description="국가, 기기, 앱 모드, 우선순위와 노출 기간을 설정합니다.">
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required label="국가코드" name="cCode" value={params.cCode || '00'} onChange={handleUpdateField} disabled={saving} size="small" inputProps={{ maxLength: 2 }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required select label="기기" name="deviceType" value={params.deviceType} onChange={handleUpdateField} disabled={saving} size="small">
                                            {APP_TAB_PROMOTION_DEVICE_TYPE_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required label="앱 모드" name="appMode" value={params.appMode || 'rm'} onChange={handleUpdateField} disabled={saving} size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth required type="number" label="우선순위" name="priority" value={params.priority} onChange={handleUpdateField} disabled={saving} size="small" inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="datetime-local" label="노출 시작" name="startTime" value={params.startTime || ''} onChange={handleUpdateField} disabled={saving} size="small" InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="datetime-local" label="노출 종료" name="endTime" value={params.endTime || ''} onChange={handleUpdateField} disabled={saving} size="small" InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appTabPromotions" search={listSearch} visible={true} curPage={listCurPage} searchText={listSearchText} searchSelect={listSearchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>} 
            </Paper>
        </Grid>
    )
}
